import { useEffect, useRef, useState } from 'react';
import { errorText, get, post } from './api.js';
import { Link, navigate, useLocation } from './router.jsx';
import { useAuth } from './session.jsx';
import { prefs, t } from './i18n.js';
import { Field, Seg, phonePretty, toE164, useLoad, Logo } from './ui.jsx';

const PHONE_ERR = t('กรอกเบอร์มือถือ 10 หลัก ขึ้นต้นด้วย 06 08 หรือ 09', 'Enter a 10-digit mobile number starting with 06, 08 or 09');
const pwRules = (v) => ({ len: v.length >= 8 && v.length <= 128, letter: /[A-Za-z฀-๿]/.test(v), digit: /\d/.test(v) }); // i18n-ok
const pwOk = (v) => Object.values(pwRules(v)).every(Boolean);
const nextPath = (search) => { const n = new URLSearchParams(search).get('next'); return n && n.startsWith('/') && !n.startsWith('//') ? n : null; };

function PasswordField({ id, label, value, onChange, error, autoComplete, rules }) {
  const [show, setShow] = useState(false);
  const r = pwRules(value);
  return (
    <Field label={label} id={id} error={error}>
      <div style={{ position: 'relative' }}>
        <input id={id} type={show ? 'text' : 'password'} autoComplete={autoComplete} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} style={{ paddingRight: 64 }} />
        <button type="button" className="btn ghost s" style={{ position: 'absolute', right: 4, top: 4, border: 0 }} onClick={() => setShow(!show)}>{show ? t('ซ่อน', 'Hide') : t('แสดง', 'Show')}</button>
      </div>
      {rules && <span className="small muted">
        <span style={{ color: r.len ? 'var(--emerald-deep)' : undefined }}>{t('อย่างน้อย 8 ตัวอักษร', 'At least 8 characters')}</span>{' '}
        <span style={{ color: r.letter ? 'var(--emerald-deep)' : undefined }}>{t('มีตัวอักษร', 'Has a letter')}</span>{' '}
        <span style={{ color: r.digit ? 'var(--emerald-deep)' : undefined }}>{t('มีตัวเลข', 'Has a number')}</span>
      </span>}
    </Field>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_ERR = t('กรอกอีเมลให้ถูกต้อง เช่น name@example.com', 'Enter a valid email, e.g. name@example.com');
// Where one-time codes go on this server: 'email', 'sms' or 'both'. Email is used whenever it is allowed.
function useOtpChannel() {
  const state = useLoad(() => get('/v1/auth/config'), []);
  const channel = state.data?.data.otp_channel || 'email';
  return { ready: !state.loading, byEmail: channel !== 'sms' };
}
const targetBody = (target) => ({ [target.kind]: target.value });
const targetText = (target) => (target.kind === 'email' ? target.value : phonePretty(target.value));

// Shared OTP step: requests a code and checks it. For register and reset_password it returns an otp_token;
// for login it returns the session.
function OtpStep({ target, purpose, onVerified, onBack }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [left, setLeft] = useState(60);
  const [devCode, setDevCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const sent = useRef(false);
  const byEmail = target.kind === 'email';
  const request = async () => {
    setError(''); setCode('');
    try {
      const { data } = await post('/v1/auth/otp/request', { ...targetBody(target), purpose });
      setDevCode(data.dev_code || null); setLeft(data.resend_after || 60);
    } catch (e) {
      setError(e.code === 'conflict' ? (byEmail ? t('อีเมลนี้สมัครสมาชิกแล้ว กรุณาเข้าสู่ระบบ', 'This email is already registered. Please sign in') : t('เบอร์นี้สมัครสมาชิกแล้ว กรุณาเข้าสู่ระบบ', 'This number is already registered. Please sign in'))
        : e.code === 'rate_limited' ? t('ขอรหัสบ่อยเกินไป หรือกรอกรหัสผิดหลายครั้ง กรุณารอ 10 นาทีแล้วลองใหม่', 'Too many code requests or wrong attempts. Please wait 10 minutes and try again') : errorText(e));
    }
  };
  useEffect(() => { if (!sent.current) { sent.current = true; request(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (left <= 0) return; const timer = setTimeout(() => setLeft(left - 1), 1000); return () => clearTimeout(timer); }, [left]);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return setError(t('กรอกรหัส 6 หลัก', 'Enter the 6-digit code'));
    setBusy(true);
    try {
      if (purpose === 'login') { const { data } = await post('/v1/auth/otp/confirm', { ...targetBody(target), code }); await onVerified(data); }
      else { const { data } = await post('/v1/auth/otp/verify', { ...targetBody(target), purpose, code }); await onVerified(data.otp_token); }
    } catch (er) { setError(errorText(er)); } finally { setBusy(false); }
  };
  return (
    <div className="stack">
      <h1>{byEmail ? t('กรอกรหัสจากอีเมล', 'Enter the code from your email') : t('กรอกรหัส OTP', 'Enter the OTP code')}</h1>
      <p className="muted">{byEmail ? t('เราส่งรหัส 6 หลักไปที่อีเมล', 'We sent a 6-digit code to') : t('รหัส 6 หลักถูกส่งไปที่เบอร์', 'A 6-digit code was sent to')} <strong className="num">{targetText(target)}</strong>{t(' ใช้ได้ภายใน 5 นาที', '. It is valid for 5 minutes.')}{byEmail ? t(' ถ้าไม่พบ ให้ดูในโฟลเดอร์อีเมลขยะ', ' If you cannot find it, check your spam folder.') : ''}</p>
      {purpose !== 'register' && !devCode && !error && <p className="muted small">{byEmail ? t('รหัสจะถูกส่งเฉพาะเมื่อมีบัญชีที่ใช้อีเมลนี้', 'A code is only sent if an account uses this email') : t('รหัสจะถูกส่งเฉพาะเมื่อมีบัญชีที่ใช้เบอร์นี้', 'A code is only sent if an account uses this number')}</p>}
      {devCode && <div className="note">{byEmail ? t('โหมดทดสอบ: ยังไม่ได้เชื่อมต่อผู้ให้บริการอีเมล รหัสของคุณคือ', 'Test mode: no email provider is connected. Your code is') : t('โหมดทดสอบ: ยังไม่ได้เชื่อมต่อผู้ให้บริการ SMS รหัสของคุณคือ', 'Test mode: no SMS provider is connected. Your code is')} <strong className="num">{devCode}</strong></div>}
      <form className="stack" onSubmit={submit} noValidate>
        <Field label={t('รหัส OTP', 'OTP code')} id="otp-code" error={error}>
          <input id="otp-code" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" value={code} aria-invalid={!!error} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
        </Field>
        <button className="btn primary" type="submit" disabled={busy}>{t('ยืนยันรหัส', 'Confirm code')}</button>
      </form>
      <div className="row between small">
        <button type="button" className="btn ghost s" disabled={left > 0} onClick={request}>{left > 0 ? t(`ส่งรหัสอีกครั้งได้ใน ${left} วินาที`, `Resend available in ${left} s`) : t('ส่งรหัสอีกครั้ง', 'Resend code')}</button>
        <button type="button" className="btn ghost s" onClick={onBack}>{byEmail ? t('เปลี่ยนอีเมล', 'Change email') : t('เปลี่ยนเบอร์โทร', 'Change phone number')}</button>
      </div>
    </div>
  );
}

// "0891112222" or "name@example.com" -> { kind, value }, or null when it is neither.
function parseIdentifier(raw) {
  const v = raw.trim();
  if (v.includes('@')) return EMAIL_RE.test(v) ? { kind: 'email', value: v.toLowerCase() } : null;
  const p = toE164(v);
  return p ? { kind: 'phone', value: p } : null;
}

export function Login() {
  const { user, signIn } = useAuth();
  const loc = useLocation();
  const { byEmail } = useOtpChannel();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [codeTarget, setCodeTarget] = useState(null);
  const done = (u) => navigate(nextPath(loc.search) || (u.role === 'admin' ? '/admin' : '/'), { replace: true });
  useEffect(() => { if (user) done(user); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const ID_ERR = t('กรอกอีเมล หรือเบอร์มือถือ 10 หลัก', 'Enter your email or 10-digit mobile number');
  const submit = async (e) => {
    e.preventDefault();
    const target = parseIdentifier(identifier);
    const errs = { phone: target ? '' : ID_ERR, password: password ? '' : t('กรอกรหัสผ่าน', 'Enter your password') };
    setErrors(errs);
    if (errs.phone || errs.password) return;
    setBusy(true);
    try { const { data } = await post('/v1/auth/login', { ...targetBody(target), password }); signIn(data); done(data.user); }
    catch (er) { setErrors({ form: er.code === 'invalid_credentials' ? t('อีเมล เบอร์โทร หรือรหัสผ่านไม่ถูกต้อง', 'Incorrect email, phone number or password') : errorText(er) }); } finally { setBusy(false); }
  };
  const withCode = () => {
    const target = parseIdentifier(identifier);
    if (!target || target.kind !== (byEmail ? 'email' : 'phone')) return setErrors({ phone: byEmail ? t('กรอกอีเมลของบัญชีก่อน แล้วกดรับรหัสอีกครั้ง', 'Enter your account email first, then request the code again') : t('กรอกเบอร์มือถือของบัญชีก่อน แล้วกดรับรหัสอีกครั้ง', 'Enter your account mobile number first, then request the code again') });
    setErrors({}); setCodeTarget(target);
  };
  if (codeTarget) return <div className="page authbox"><div className="card"><OtpStep target={codeTarget} purpose="login" onVerified={async (data) => { signIn(data); done(data.user); }} onBack={() => setCodeTarget(null)} /></div></div>;
  return (
    <div className="page authbox">
      <div className="card stack">
        <Logo className="authlogo" />
        <h1>{t('เข้าสู่ระบบ', 'Sign in')}</h1>
        {errors.form && <div className="note bad" role="alert">{errors.form}</div>}
        <form className="stack" onSubmit={submit} noValidate>
          <Field label={t('อีเมลหรือเบอร์โทรศัพท์', 'Email or phone number')} id="login-phone" error={errors.phone}><input id="login-phone" type="text" inputMode="email" autoComplete="username" placeholder={t('name@example.com หรือ 08X-XXX-XXXX', 'name@example.com or 08X-XXX-XXXX')} value={identifier} aria-invalid={!!errors.phone} onChange={(e) => setIdentifier(e.target.value)} /></Field>
          <PasswordField id="login-pw" label={t('รหัสผ่าน', 'Password')} value={password} onChange={setPassword} error={errors.password} autoComplete="current-password" />
          <button className="btn primary" type="submit" disabled={busy}>{t('เข้าสู่ระบบ', 'Sign in')}</button>
          <button className="btn ghost" type="button" onClick={withCode}>{byEmail ? t('รับรหัสเข้าสู่ระบบทางอีเมล', 'Get a sign-in code by email') : t('รับรหัสเข้าสู่ระบบทาง SMS', 'Get a sign-in code by SMS')}</button>
        </form>
        <div className="row between small"><Link to="/forgot">{t('ลืมรหัสผ่าน', 'Forgot password')}</Link><span>{t('ยังไม่มีบัญชี', 'No account yet?')} <Link to={`/register${loc.search}`}>{t('สมัครสมาชิก', 'Create account')}</Link></span></div>
      </div>
    </div>
  );
}

export function Register() {
  const { signIn } = useAuth();
  const loc = useLocation();
  const { byEmail } = useOtpChannel();
  const picked = new URLSearchParams(loc.search).get('role') || prefs.role();
  const [form, setForm] = useState({ role: ['owner', 'agent', 'buyer'].includes(picked) ? picked : 'owner', full_name: '', phone: '', email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [otpTarget, setOtpTarget] = useState(null);
  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    const p = toE164(form.phone);
    const name = form.full_name.trim(), em = form.email.trim().toLowerCase();
    const errs = {
      full_name: name.length >= 2 && name.length <= 120 ? '' : t('กรอกชื่อและนามสกุล', 'Enter your first and last name'),
      phone: p ? '' : PHONE_ERR,
      email: (em ? EMAIL_RE.test(em) : !byEmail) ? '' : (byEmail && !em ? t('กรอกอีเมล เราจะส่งรหัสยืนยันไปที่อีเมลนี้', 'Enter your email. We will send the verification code there') : EMAIL_ERR),
      password: pwOk(form.password) ? '' : t('รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร มีทั้งตัวอักษรและตัวเลข', 'Password must be at least 8 characters with both letters and numbers'),
    };
    setErrors(errs);
    if (Object.values(errs).some(Boolean)) return;
    setOtpTarget(byEmail ? { kind: 'email', value: em } : { kind: 'phone', value: p });
  };
  const finish = async (otp_token) => {
    const em = form.email.trim().toLowerCase();
    const { data } = await post('/v1/auth/register', { phone: toE164(form.phone), otp_token, password: form.password, role: form.role, full_name: form.full_name.trim(), ...(em ? { email: em } : {}) });
    signIn(data);
    navigate(nextPath(loc.search) || (form.role === 'buyer' ? '/' : '/me/verify'), { replace: true });
  };
  if (otpTarget) return <div className="page authbox"><div className="card"><OtpStep target={otpTarget} purpose="register" onVerified={finish} onBack={() => setOtpTarget(null)} /></div></div>;
  return (
    <div className="page authbox">
      <div className="card stack">
        <Logo className="authlogo" />
        <h1>{t('สมัครสมาชิก', 'Create account')}</h1>
        <form className="stack" onSubmit={submit} noValidate>
          <div className="f"><span className="lab">{t('คุณคือ', 'You are')}</span><Seg label={t('บทบาท', 'Role')} value={form.role} onChange={set('role')} options={[['owner', t('เจ้าของ', 'Owner')], ['agent', t('นายหน้า', 'Agent')], ['buyer', t('ผู้ซื้อ ผู้เช่า', 'Buyer or renter')]]} /></div>
          <Field label={t('ชื่อและนามสกุล', 'First and last name')} id="reg-name" error={errors.full_name}><input id="reg-name" type="text" autoComplete="name" value={form.full_name} aria-invalid={!!errors.full_name} onChange={(e) => set('full_name')(e.target.value)} /></Field>
          <Field label={byEmail ? t('อีเมล (ใช้รับรหัสยืนยันและการแจ้งเตือน)', 'Email (for verification codes and notifications)') : t('อีเมล (ไม่บังคับ ใช้รับการแจ้งเตือน)', 'Email (optional, for notifications)')} id="reg-email" error={errors.email}><input id="reg-email" type="email" autoComplete="email" placeholder="name@example.com" value={form.email} aria-invalid={!!errors.email} onChange={(e) => set('email')(e.target.value)} /></Field>
          <Field label={t('เบอร์โทรศัพท์', 'Phone number')} id="reg-phone" error={errors.phone} hint={byEmail ? t('ใช้เป็นช่องทางติดต่อบนประกาศ', 'Thai mobile number, used as a contact channel on listings') : undefined}><input id="reg-phone" type="tel" inputMode="numeric" autoComplete="tel-national" placeholder="08X-XXX-XXXX" value={form.phone} aria-invalid={!!errors.phone} onChange={(e) => set('phone')(e.target.value)} /></Field>
          <PasswordField id="reg-pw" label={t('รหัสผ่าน', 'Password')} value={form.password} onChange={set('password')} error={errors.password} autoComplete="new-password" rules />
          <p className="muted small">{t('เมื่อสมัครสมาชิก ถือว่าคุณยอมรับ', 'By creating an account you accept the')} <Link to="/terms">{t('ข้อกำหนดการใช้งาน', 'Terms of Use')}</Link> {t('และ', 'and the')} <Link to="/privacy">{t('นโยบายความเป็นส่วนตัว', 'Privacy Policy')}</Link></p>
          <button className="btn primary" type="submit">{byEmail ? t('ส่งรหัสยืนยันไปที่อีเมล', 'Send verification code to email') : t('ขอรหัส OTP', 'Request OTP')}</button>
        </form>
        <div className="small">{t('มีบัญชีแล้ว', 'Already have an account?')} <Link to={`/login${loc.search}`}>{t('เข้าสู่ระบบ', 'Sign in')}</Link></div>
      </div>
    </div>
  );
}

export function Forgot() {
  const { byEmail } = useOtpChannel();
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [stage, setStage] = useState('start'); // start | otp | reset | done
  const [target, setTarget] = useState(null);
  const [token, setToken] = useState(null);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [errs, setErrs] = useState({});
  const start = (e) => {
    e.preventDefault();
    const parsed = parseIdentifier(value);
    if (!parsed || parsed.kind !== (byEmail ? 'email' : 'phone')) return setError(byEmail ? EMAIL_ERR : PHONE_ERR);
    setError(''); setTarget(parsed); setStage('otp');
  };
  const save = async (e) => {
    e.preventDefault();
    const er = { pw: pwOk(pw) ? '' : t('รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร มีทั้งตัวอักษรและตัวเลข', 'Password must be at least 8 characters with both letters and numbers'), pw2: pw === pw2 ? '' : t('รหัสผ่านทั้งสองช่องไม่ตรงกัน', 'The two passwords do not match') };
    setErrs(er);
    if (er.pw || er.pw2) return;
    try { await post('/v1/auth/reset_password', { otp_token: token, password: pw }); setStage('done'); } catch (x) { setErrs({ form: errorText(x) }); }
  };
  return (
    <div className="page authbox">
      <div className="card stack">
        {stage === 'start' && <>
          <h1>{t('ลืมรหัสผ่าน', 'Forgot password')}</h1>
          <p className="muted">{byEmail ? t('กรอกอีเมลที่ใช้สมัคร เราจะส่งรหัสยืนยันไปทางอีเมล', 'Enter the email you registered with. We will email you a verification code') : t('กรอกเบอร์โทรที่ใช้สมัคร เราจะส่งรหัส OTP ไปทาง SMS', 'Enter the phone number you registered with. We will send an OTP by SMS')}</p>
          <form className="stack" onSubmit={start} noValidate>
            <Field label={byEmail ? t('อีเมล', 'Email') : t('เบอร์โทรศัพท์', 'Phone number')} id="forgot-phone" error={error}><input id="forgot-phone" type={byEmail ? 'email' : 'tel'} inputMode={byEmail ? 'email' : 'numeric'} autoComplete={byEmail ? 'email' : 'tel-national'} placeholder={byEmail ? 'name@example.com' : '08X-XXX-XXXX'} value={value} aria-invalid={!!error} onChange={(e) => setValue(e.target.value)} /></Field>
            <button className="btn primary" type="submit">{byEmail ? t('ส่งรหัสไปที่อีเมล', 'Send code to email') : t('ขอรหัส OTP', 'Request OTP')}</button>
          </form>
          <Link to="/login" className="small">{t('กลับไปเข้าสู่ระบบ', 'Back to sign in')}</Link>
        </>}
        {stage === 'otp' && <OtpStep target={target} purpose="reset_password" onVerified={async (tok) => { setToken(tok); setStage('reset'); }} onBack={() => setStage('start')} />}
        {stage === 'reset' && <>
          <h1>{t('ตั้งรหัสผ่านใหม่', 'Set a new password')}</h1>
          {errs.form && <div className="note bad" role="alert">{errs.form}</div>}
          <form className="stack" onSubmit={save} noValidate>
            <PasswordField id="reset-pw" label={t('รหัสผ่านใหม่', 'New password')} value={pw} onChange={setPw} error={errs.pw} autoComplete="new-password" rules />
            <PasswordField id="reset-pw2" label={t('ยืนยันรหัสผ่านใหม่', 'Confirm new password')} value={pw2} onChange={setPw2} error={errs.pw2} autoComplete="new-password" />
            <button className="btn primary" type="submit">{t('บันทึกรหัสผ่านใหม่', 'Save new password')}</button>
          </form>
        </>}
        {stage === 'done' && <>
          <h1>{t('เปลี่ยนรหัสผ่านแล้ว', 'Password changed')}</h1>
          <p className="muted">{t('ระบบออกจากระบบทุกอุปกรณ์ให้แล้ว เข้าสู่ระบบด้วยรหัสผ่านใหม่ได้เลย', 'You have been signed out on all devices. Sign in with your new password.')}</p>
          <Link className="btn primary" to="/login">{t('ไปหน้าเข้าสู่ระบบ', 'Go to sign in')}</Link>
        </>}
      </div>
    </div>
  );
}
